/**
 * AnalysisSpec, in the browser.
 *
 * These types are a loose structural mirror of
 * `app/semantic/analysis_spec.schema.json`, and deliberately nothing more.
 * Every place the schema has a closed set — operators, measure functions,
 * visualizations, units, actors — the type here says `string`, because the
 * legal members come from `describe()` at runtime. A TypeScript enum would be
 * a second copy of the truth, and the second copy is always the one that goes
 * stale.
 *
 * The builder's state *is* a spec. Nothing translates between a UI model and
 * an analysis model, because there is only one model. That is what makes the
 * round trip cheap: forward is `JSON.stringify`, back is `setSpec(parsed)`.
 */

export interface Quantity {
  value: number
  unit: string
}

export interface Comparison {
  property: string
  step?: string
  op: string
  value?: unknown
  unit?: string
}

export interface SequenceStep {
  id: string
  event: string
  within: Quantity
  after?: string
  by?: string
  where?: Condition
}

export interface SequenceCondition {
  sequence: { steps: SequenceStep[] }
}

export interface And { and: Condition[] }
export interface Or { or: Condition[] }
export interface Not { not: Condition }

export type Condition = Comparison | SequenceCondition | And | Or | Not

/** `{property}` resolves at the subject; `{step, property}` at a sequence step. */
export interface PropertyOperand {
  property: string
  step?: string
}

export type MeasureOperand = PropertyOperand | Condition

export interface Measure {
  id: string
  fn: string
  label?: string
  of?: MeasureOperand
}

export interface Bins {
  width: number
  unit?: string
}

export interface Breakdown {
  property?: string
  step?: string
  label?: string
  bins?: Bins
  limit?: number
  min_subjects?: number
  buckets?: unknown[]
}

export interface Visualize {
  as: string
  x?: string
  y?: string
  value?: string
}

export interface AnalysisSpec {
  version: string
  catalog_version?: string
  title?: string
  question?: string
  subject: { type: string }
  where?: Condition
  measures?: Measure[]
  breakdowns?: Breakdown[]
  visualize?: Visualize
}

// ------------------------------------------------------------------- shapes

const has = (v: unknown, k: string): boolean =>
  typeof v === 'object' && v !== null && k in (v as Record<string, unknown>)

export const isAnd = (c: unknown): c is And => has(c, 'and')
export const isOr = (c: unknown): c is Or => has(c, 'or')
export const isNot = (c: unknown): c is Not => has(c, 'not')
export const isSequence = (c: unknown): c is SequenceCondition => has(c, 'sequence')
export const isComparison = (c: unknown): c is Comparison =>
  has(c, 'property') && has(c, 'op')
export const isPropertyOperand = (o: unknown): o is PropertyOperand =>
  has(o, 'property') && !has(o, 'op')

// ------------------------------------------------------------- key ordering

// The order the example specs use. Ordering is cosmetic to a parser and
// everything to a human reading the debug panel — and it is what lets the
// round trip be checked byte-for-byte against the files on disk rather than
// merely "semantically equal", which is a much weaker claim.
const SPEC_KEYS = ['version', 'catalog_version', 'title', 'question', 'subject',
  'where', 'measures', 'breakdowns', 'visualize']
const MEASURE_KEYS = ['id', 'fn', 'label', 'of']
const BREAKDOWN_KEYS = ['property', 'step', 'label', 'bins', 'limit',
  'min_subjects', 'buckets']
const COMPARISON_KEYS = ['property', 'step', 'op', 'value', 'unit']
const STEP_KEYS = ['id', 'event', 'within', 'after', 'by', 'where']
const VISUALIZE_KEYS = ['as', 'x', 'y', 'value']

/** Known keys first, in order; anything unrecognised kept, never dropped. */
function ordered<T>(obj: T, keys: string[]): T {
  const src = obj as unknown as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const k of keys) if (src[k] !== undefined) out[k] = src[k]
  for (const k of Object.keys(src)) {
    if (!(k in out) && src[k] !== undefined) out[k] = src[k]
  }
  return out as unknown as T
}

function orderCondition(c: Condition): Condition {
  if (isAnd(c)) return { and: c.and.map(orderCondition) }
  if (isOr(c)) return { or: c.or.map(orderCondition) }
  if (isNot(c)) return { not: orderCondition(c.not) }
  if (isSequence(c)) {
    return { sequence: { steps: c.sequence.steps.map(orderStep) } }
  }
  return ordered(c, COMPARISON_KEYS)
}

function orderStep(s: SequenceStep): SequenceStep {
  const out = { ...s }
  if (out.where) out.where = orderCondition(out.where)
  return ordered(out, STEP_KEYS)
}

function orderMeasure(m: Measure): Measure {
  const out = { ...m }
  if (out.of !== undefined) {
    out.of = isPropertyOperand(out.of)
      ? ordered(out.of, ['step', 'property'])
      : orderCondition(out.of as Condition)
  }
  return ordered(out, MEASURE_KEYS)
}

/** The spec with every object's keys in canonical order. Value-preserving. */
export function orderSpec(spec: AnalysisSpec): AnalysisSpec {
  const out = { ...spec }
  if (out.where) out.where = orderCondition(out.where)
  if (out.measures) out.measures = out.measures.map(orderMeasure)
  if (out.breakdowns) out.breakdowns = out.breakdowns.map((b) => ordered(b, BREAKDOWN_KEYS))
  if (out.visualize) out.visualize = ordered(out.visualize, VISUALIZE_KEYS)
  return ordered(out, SPEC_KEYS)
}

/** What the debug panel shows, and what is POSTed. */
export function serialize(spec: AnalysisSpec): string {
  return JSON.stringify(orderSpec(spec), null, 2)
}

function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortDeep)
  if (value && typeof value === 'object') {
    const src = value as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(src).sort()) out[k] = sortDeep(src[k])
    return out
  }
  return value
}

/** Semantic equality: same content, regardless of key order. */
export function sameSpec(a: AnalysisSpec, b: AnalysisSpec): boolean {
  return JSON.stringify(sortDeep(a)) === JSON.stringify(sortDeep(b))
}

// -------------------------------------------------------------- where slot

/**
 * The `where` slot as a flat list.
 *
 * A single condition is stored bare and several are stored under `and` — the
 * examples do both, so the builder has to do both or it cannot round-trip
 * them. `conditions`/`withConditions` are the only two places that know it.
 */
export function conditions(spec: AnalysisSpec): Condition[] {
  const w = spec.where
  if (!w) return []
  return isAnd(w) ? w.and : [w]
}

export function withConditions(spec: AnalysisSpec, list: Condition[]): AnalysisSpec {
  const out = { ...spec }
  if (list.length === 0) delete out.where
  else if (list.length === 1) out.where = list[0]
  else out.where = { and: list }
  return orderSpec(out)
}

export function withMeasures(spec: AnalysisSpec, list: Measure[]): AnalysisSpec {
  return orderSpec({ ...spec, measures: list })
}

export function withBreakdowns(spec: AnalysisSpec, list: Breakdown[]): AnalysisSpec {
  const out = { ...spec }
  if (list.length === 0) delete out.breakdowns
  else out.breakdowns = list
  return orderSpec(out)
}

// ------------------------------------------------------------------- steps

export interface KnownStep {
  id: string
  event: string
  /** Where the step is defined — a filter, or the measure that owns it. */
  from: string
}

/**
 * Every sequence step the spec defines, wherever it sits.
 *
 * Both slots have to be searched: `q3_rate` defines step `prog` inside a rate
 * measure and then references it from a *different* measure's operand.
 */
export function stepsIn(spec: AnalysisSpec): KnownStep[] {
  const out: KnownStep[] = []
  const walk = (c: Condition | undefined, from: string): void => {
    if (!c) return
    if (isAnd(c)) c.and.forEach((k) => walk(k, from))
    else if (isOr(c)) c.or.forEach((k) => walk(k, from))
    else if (isNot(c)) walk(c.not, from)
    else if (isSequence(c)) {
      for (const s of c.sequence.steps) out.push({ id: s.id, event: s.event, from })
    }
  }
  walk(spec.where, 'filter')
  for (const m of spec.measures ?? []) {
    if (m.of && !isPropertyOperand(m.of)) walk(m.of as Condition, m.label || m.id)
  }
  return out
}

/** An id matching the schema's `^[a-z][a-z0-9_]*$`, unique among `taken`. */
export function slug(text: string, taken: string[] = []): string {
  let base = text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  if (!base || !/^[a-z]/.test(base)) base = `x_${base}`.replace(/_+$/, '')
  let id = base
  for (let n = 2; taken.includes(id); n += 1) id = `${base}_${n}`
  return id
}

// ------------------------------------------------------- authorable subset

/**
 * Why the builder cannot render this spec faithfully.
 *
 * Empty means "load it". Non-empty means refuse: silently dropping a clause
 * the controls cannot show would turn a load into a data-losing edit, and the
 * analyst would never know their question had changed.
 */
export function unsupported(spec: AnalysisSpec): string[] {
  const out: string[] = []
  if (!spec || typeof spec !== 'object') return ['Not an object.']
  if (spec.version !== '1.0') {
    out.push(`Spec version ${String(spec.version)} — this builder authors 1.0.`)
  }
  if (!spec.subject?.type) out.push('No subject.')

  const sequence = (c: SequenceCondition, where: string): void => {
    const steps = c.sequence?.steps ?? []
    if (steps.length !== 1) {
      out.push(`${where}: a ${steps.length}-step sequence. The builder authors one step.`)
    }
    for (const s of steps) {
      if (s.where) out.push(`${where}: step "${s.id}" carries its own filter.`)
      if (s.after) out.push(`${where}: step "${s.id}" is anchored with "after".`)
    }
  }
  const condition = (c: Condition, where: string): void => {
    if (isAnd(c)) out.push(`${where}: nested "all of".`)
    else if (isOr(c)) out.push(`${where}: "any of" is not authorable yet.`)
    else if (isNot(c)) out.push(`${where}: "not" is not authorable yet.`)
    else if (isSequence(c)) sequence(c, where)
    else if (isComparison(c)) {
      if (c.step) out.push(`${where}: filters on a sequence step are not authorable yet.`)
    } else out.push(`${where}: unrecognised condition.`)
  }

  conditions(spec).forEach((c, i) => condition(c, `Filter ${i + 1}`))

  for (const m of spec.measures ?? []) {
    const at = `Measure "${m.id}"`
    if (m.of === undefined) continue
    if (isPropertyOperand(m.of)) continue
    if (isSequence(m.of)) sequence(m.of, at)
    else out.push(`${at}: measured over a condition the builder cannot show.`)
  }
  for (const b of spec.breakdowns ?? []) {
    if (b.buckets) out.push('Bucketed breakdowns are not authorable yet.')
    if (b.step) out.push('Breakdowns on a sequence step are not authorable yet.')
  }
  return out
}
